/**
 * The retention window and its change (mission 10 WP15 T084; FR-005, FR-006, FR-017, FR-022; D.2, A.3;
 * contracts/retention-api.md).
 *
 * Typed wrappers for `GET` and `PATCH /v1/tenant/retention`. The types follow the example bodies in
 * `backend/internal/httpapi/testdata/m10/` member for member (`httpapi/m10_types.go`; their SPA copies are
 * `src/test/fixtures/m10/`, pinned by `fixtures-m10.test.ts`).
 *
 * - Every period the screens render is a member of `terms` (C-013, FR-022), never a literal.
 * - A member the contract prints as null is `| null`: `last_changed_at` when the window never changed,
 *   `due_at` at retention 0 while the first transmit request is pending (U26), and the open requests.
 * - The PATCH body is built key by key (mission 6 C-006): the acknowledgment and the confirmed count travel
 *   only when given. The 409 `retention-confirmation-required` (D.2) is an ApiError whose problem carries
 *   `due_count`, `runs` and `truncated` (spec amendment 26); `lib/queries.ts` surfaces them.
 */
import { InvalidIdError, api, type ProblemMembers } from '../api'
import type { RunState } from './runs'

/** `retention.CurrentTerms()`: every period the SPA states (C-013, FR-022). */
export interface RetentionTerms {
  default_days: number
  min_days: number
  max_days: number
  class2_years: number
  expiry_notice_days: number
  digest_min_window_days: number
  fulfilment_days: number
  key_pending_days: number
  zero_fallback_hours: number
  sweep_interval_minutes: number
}

/** The Class 1 kinds a run still holds, derived from its rows (contracts/retention-api.md "holds"). */
export type RetentionHold = 'upload' | 'generated' | 'findings' | 'validation_report' | 'conversation' | 'rejection_text'

export const RETENTION_HOLDS: readonly RetentionHold[] = [
  'upload',
  'generated',
  'findings',
  'validation_report',
  'conversation',
  'rejection_text',
]

/** One terminal run whose Class 1 is held, ordered (due_at, id). */
export interface RetentionRun {
  id: string
  short_id: string
  state: RunState
  /** Null for a legacy terminal run that recorded none (mission 10 WP17; WP07 review); never a zero time. */
  completed_at: string | null
  /** EffectiveDue under the current window; null at retention 0 while the first transmit request is pending. */
  due_at: string | null
  holds: RetentionHold[]
}

/** An export request: the window's open one, and the request member of POST /v1/tenant/export (FR-011). */
export interface ExportRequest {
  id: string
  state: string
  requested_at: string
  due_at: string
  fulfilled_at: string | null
}

/** The window's open deletion request (only ever read by a test or the request's own answer). */
export interface DeletionRequestSummary {
  id: string
  state: string
  requested_at: string
  due_at: string
  completed_at: string | null
}

/** GET /v1/tenant/retention (FR-006). */
export interface RetentionWindow {
  retention_days: number
  last_changed_at: string | null
  terms: RetentionTerms
  runs: RetentionRun[]
  next_cursor: string | null
  export_request: ExportRequest | null
  deletion_request: DeletionRequestSummary | null
}

/** PATCH /v1/tenant/retention's body (FR-005). */
export interface RetentionPatchBody {
  retention_days: number
  /** Required true at 0 (UC-04). */
  acknowledge_zero?: boolean
  /** The `due_count` of the refusal the reporter confirmed. */
  confirm_due_count?: number
}

/** The PATCH's 200: `recomputed` is 0 for an unchanged value, which writes nothing. */
export interface RetentionPatchResult {
  retention_days: number
  previous_days: number
  changed_at: string
  recomputed: number
}

/** One run of the 409 `retention-confirmation-required` (at most 100; spec amendment 26). */
export type RetentionPreviewRun = NonNullable<ProblemMembers['runs']>[number]

/** A whole, non-negative number of days or count; anything else is refused before any fetch. */
function whole(value: number, param: string): number {
  if (!Number.isInteger(value) || value < 0) throw new InvalidIdError(param)
  return value
}

export async function getRetention(cursor?: string): Promise<RetentionWindow> {
  const path = '/tenant/retention'
  return api<RetentionWindow>(cursor === undefined ? path : `${path}?${new URLSearchParams({ cursor }).toString()}`)
}

export async function patchRetention(body: RetentionPatchBody): Promise<RetentionPatchResult> {
  const clean: RetentionPatchBody = { retention_days: whole(body.retention_days, 'retention days') }
  if (body.acknowledge_zero !== undefined) clean.acknowledge_zero = body.acknowledge_zero
  if (body.confirm_due_count !== undefined) clean.confirm_due_count = whole(body.confirm_due_count, 'due count')
  return api<RetentionPatchResult>('/tenant/retention', { method: 'PATCH', body: JSON.stringify(clean) })
}
